import { useMemo, useState } from 'react'
import { useConnectApp, useConnections, useToolkits } from '@/features/integrations'
import { indexConnectionsBySlug, toCard } from './toolkitCards.js'
import { filterToolkitCards } from './filterToolkitCards.js'
import { toolkitCategoryOptions } from './toolkitCategoryOptions.js'
import { rankToolkitCards } from './rankToolkitCards.js'
import { useCardWindow } from './useCardWindow.js'

/**
 * The browse-and-connect surface, backed by the live Composio catalogue.
 *
 * Search and category filtering stay client-side because the provider's
 * toolkit listing takes no search parameter; the catalogue is cached, so
 * filtering in memory is cheaper than a round trip.
 */
export function useIntegrationsMarketplaceModel() {
  const [categoryId, setCategoryId] = useState('all')
  const [query, setQuery] = useState('')

  const toolkits = useToolkits()
  const connections = useConnections()
  const connect = useConnectApp()

  const cards = useMemo(() => {
    const bySlug = indexConnectionsBySlug(connections.data)
    return (toolkits.data ?? [])
      .map((toolkit) => toCard(toolkit, bySlug.get(toolkit.slug)))
      .filter((card) => !card.noAuth)
  }, [toolkits.data, connections.data])

  const filteredApps = useMemo(
    () => rankToolkitCards(filterToolkitCards(cards, { categoryId, query })),
    [cards, categoryId, query],
  )

  const categories = useMemo(() => toolkitCategoryOptions(cards), [cards])

  const window = useCardWindow(filteredApps, `${categoryId}::${query}`)

  return {
    categoryId,
    setCategoryId,
    categories,
    query,
    setQuery,
    filteredApps,
    gridApps: window.visible,
    showPopularSection: false,
    hasMore: window.hasMore,
    showMore: window.showMore,
    visibleCount: window.visible.length,
    matchCount: filteredApps.length,
    totalCount: cards.length,
    isLoading: toolkits.isLoading,
    error: toolkits.error,
    connectError: connect.isError ? 'The connection could not be started. Please try again.' : null,
    clearConnectError: connect.reset,
    onConnect: (slug) => {
      connect.reset()
      connect.mutate({ toolkitSlug: slug })
    },
    connectingSlug: connect.isPending ? connect.variables?.toolkitSlug : null,
  }
}
